/**
 * User menu in the top-right — surfaces display name + role and a
 * logout action that clears /me cache and navigates back to /login.
 */
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, Link } from "@tanstack/react-router";
import { LogOut, Settings, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useSession } from "@/store/session";
import { useInvalidateMe } from "@/hooks/useMe";
import { logout } from "@/api/auth";
import { clearAllPersonCreateDrafts } from "@/components/people/personCreateDraft";

export function UserMenu(): React.ReactElement | null {
  const { t } = useTranslation();
  const user = useSession((s) => s.user);
  const clear = useSession((s) => s.clear);
  const invalidateMe = useInvalidateMe();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      clearAllPersonCreateDrafts();
      clear();
      invalidateMe();
      void navigate({ to: "/login" });
    },
  });

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="user-menu-trigger" className="gap-2">
          <User className="h-4 w-4" />
          <span className="text-sm">{user.display_name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span>{user.display_name}</span>
          <span className="text-muted-foreground text-xs">{t(`role.${user.role}`)}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex w-full items-center gap-2">
            <Settings className="h-4 w-4" />
            {t("layout.userMenu.settings")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => mutation.mutate()}
          data-testid="user-menu-logout"
          disabled={mutation.isPending}
          className="flex items-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          {t("layout.userMenu.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
